/**
 * @file unityManifestRouter — makes the UNCHANGED Unity build load an arbitrary
 * weekly manifest by redirecting, in the browser, the one network request Unity
 * makes for its weekly manifest.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHY THIS IS THE RIGHT LAYER (verified against the shipped build)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Unity's UnityWebRequest downloads the manifest via Module.cachedFetch →
 *  Module.fetchWithProgress, and fetchWithProgress ultimately calls the GLOBAL
 *  `fetch(resource, init)` (see sky-city-rush.loader.js). So patching
 *  window.fetch intercepts the manifest download. We also patch XHR for safety.
 *
 *  The manifest URL is baked into the build (ThisWeekConfig.ManifestUrl →
 *  .../gen-hackathon/current.json) with no runtime override, and we must not
 *  modify the build — hence this redirect. Only the manifest request is
 *  rewritten; the assets it references are downloaded by Unity as usual.
 *
 *  Current and archived are symmetric: the page always sets the active manifest
 *  URL (current.json for the current week, archive/{id}.json for an archived
 *  week) and this module points Unity's request at it.
 */

import { UNITY_WEEKLY_MANIFEST_URL } from '../config/scrWeeklyConfig';
import { logLoadingManifest } from './diagnostics';

/** The manifest Unity should actually receive. null → pass through unchanged. */
let overrideUrl = null;

/** Saved originals so we can fully restore them. */
let originalFetch = null;
let originalXhrOpen = null;
let installed = false;

/** Extract a string URL from fetch's various input shapes. */
function urlFromInput(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input === 'object') {
    if (typeof input.url === 'string') return input.url; // Request
    if (typeof input.href === 'string') return input.href; // URL
    try {
      return String(input);
    } catch (e) {
      return '';
    }
  }
  return '';
}

/**
 * Does this URL point at the Unity build's baked weekly manifest? Matches the
 * exact configured URL and, defensively, any request whose path ends in
 * "/current.json" (ignoring query strings Unity may append).
 * @param {string} url
 */
function isWeeklyManifestRequest(url) {
  if (typeof url !== 'string' || !url) return false;
  const noQuery = url.split('?')[0];
  if (noQuery === UNITY_WEEKLY_MANIFEST_URL.split('?')[0]) return true;
  return noQuery.endsWith('/current.json');
}

/**
 * Given the URL Unity asked for, return the URL it should actually receive.
 * @param {string} url
 * @returns {string}
 */
function rewrite(url) {
  if (!isWeeklyManifestRequest(url)) return url;

  if (overrideUrl && overrideUrl !== url) {
    logLoadingManifest(overrideUrl);
    // eslint-disable-next-line no-console
    console.log(`[SCR Weekly][net] Unity requested ${url} → serving ${overrideUrl}`);
    return overrideUrl;
  }

  // eslint-disable-next-line no-console
  console.log(`[SCR Weekly][net] Unity manifest request (no override): ${url}`);
  return url;
}

/**
 * Set which manifest the Unity build should receive. Called for BOTH current
 * and archived weeks — that URL is the only thing that differs between them.
 * @param {string|null} url
 */
export function setActiveManifestUrl(url) {
  overrideUrl = url || null;
}

/** The current override (mainly for tests/diagnostics). */
export function getActiveManifestUrl() {
  return overrideUrl;
}

/** Install the fetch + XHR interceptors. Idempotent. No-op on the server. */
export function installManifestInterceptor() {
  if (installed || typeof window === 'undefined') return;

  // fetch() — keep the EXACT original for a clean restore, bound copy for calls.
  if (typeof window.fetch === 'function') {
    originalFetch = window.fetch;
    const boundFetch = originalFetch.bind(window);
    window.fetch = function patchedFetch(input, init) {
      try {
        const url = urlFromInput(input);
        const newUrl = rewrite(url);
        if (newUrl !== url) {
          // Replace the request with the redirected URL, preserving init.
          if (typeof input === 'string' || (input && typeof input.href === 'string')) {
            return boundFetch(newUrl, init);
          }
          if (input && typeof Request !== 'undefined' && input instanceof Request) {
            return boundFetch(new Request(newUrl, input), init);
          }
          return boundFetch(newUrl, init);
        }
      } catch (e) {
        /* fall through to original behaviour on any failure */
      }
      return boundFetch(input, init);
    };
  }

  // XMLHttpRequest.open — belt-and-suspenders for any XHR-based path.
  if (
    typeof XMLHttpRequest !== 'undefined' &&
    XMLHttpRequest.prototype &&
    typeof XMLHttpRequest.prototype.open === 'function'
  ) {
    originalXhrOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
      let finalUrl = url;
      try {
        if (typeof url === 'string') finalUrl = rewrite(url);
      } catch (e) {
        finalUrl = url;
      }
      return originalXhrOpen.call(this, method, finalUrl, ...rest);
    };
  }

  installed = true;
}

/** Restore the original fetch / XHR and clear the override. */
export function uninstallManifestInterceptor() {
  if (typeof window !== 'undefined') {
    if (originalFetch) window.fetch = originalFetch;
    if (originalXhrOpen && typeof XMLHttpRequest !== 'undefined') {
      XMLHttpRequest.prototype.open = originalXhrOpen;
    }
  }
  originalFetch = null;
  originalXhrOpen = null;
  overrideUrl = null;
  installed = false;
}
